import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  IconButton,
  Button,
  TextField,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Checkbox,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { ADD_ROLE, DELETE_ROLE, GET_ALL_FEATURES, GET_ALL_ROLES, UPDATE_ROLE } from "../queries";
import CloseIcon from "@mui/icons-material/Close";
import RoleDialog from "./RoleDialog";
import { useAuth } from "../../../hooks/useAuth";
import { enqueueSnackbar } from "notistack";

interface Role {
  role_id: string;
  role_name: string;
  features: {
    feature_id: string;
    feature_name: string;
    create_access: boolean;
    read_access: boolean;
    update_access: boolean;
    delete_access: boolean;
  }[];
}

interface RolesProps {
  createdBy: string;
}

const Roles: React.FC<RolesProps> = ({ createdBy }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<string | null>(null);
  const auth = useAuth();

  const { data, refetch: refetchRoles } = useQuery(GET_ALL_ROLES, {
    onCompleted: (data) => {
      console.log("Roles after refetch:", data.getAllRoles);
      setRoles(data.getAllRoles);
    },
    fetchPolicy: "network-only", // good
  });


  const [deleteRole] = useMutation(DELETE_ROLE, {
    onCompleted: async () => {
      enqueueSnackbar("Role deleted successfully", { variant: "success" });

      // Wait for roles to refetch and update local state
      const { data } = await refetchRoles();
      if (data?.getAllRoles) {
        setRoles(data.getAllRoles);
      }

      setRoleToDelete(null);
      setOpenDeleteDialog(false);
    },
    onError: (err) => {
      console.error("Delete Role Error:", err.message);
      enqueueSnackbar(`Failed to delete role: ${err.message}`, { variant: "error" });
    },
  });


  const handleOpenDialog = (mode: "create" | "edit", role?: Role) => {
    setDialogMode(mode);
    setSelectedRole(mode === "edit" && role ? role : null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRole(null);
    refetchRoles();
  };

  const filteredRoles = roles.filter((role) =>
    role.role_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const confirmDeleteRole = (roleId: string) => {
    setRoleToDelete(roleId);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!roleToDelete) return;
    try {
      await deleteRole({ variables: { roleId: roleToDelete } });
    } catch (error) {
      console.error("Delete Role Error:", error);
    }
  };



  return (
    <>
      <Paper elevation={3} sx={{ borderRadius: 2, p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">Roles</Typography>
          {auth.canCreate("USER") && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ backgroundColor: "#E60012" }}
              onClick={() => handleOpenDialog("create")}
            >
              Add Role
            </Button>
          )}
        </Box>

        <Box mb={2}>
          <TextField
            placeholder="Search by role name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
          />
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#E60012" }}>
                <TableCell sx={{ color: "#f5f5f5", fontWeight: "bold" }}>ID</TableCell>
                <TableCell sx={{ color: "#f5f5f5", fontWeight: "bold" }}>Name</TableCell>
                <TableCell sx={{ color: "#f5f5f5", fontWeight: "bold" }}>Role</TableCell>
                {(auth.canCreate("USER") || auth.canUpdate("USER") || auth.canDelete("USER")) && (
                  <TableCell sx={{ color: "#f5f5f5", fontWeight: "bold" }}>Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRoles.map((role, idx) => (
                <TableRow
                  key={role.role_id}
                  sx={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f5f5f5" }}
                >
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{role.role_name}</TableCell>
                  <TableCell>{role.role_name}</TableCell>
                  {(auth.canCreate("USER") || auth.canUpdate("USER") || auth.canDelete("USER")) && (
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {auth.canUpdate("USER") && (
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleOpenDialog("edit", role)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {auth.canDelete("USER") && (
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => confirmDeleteRole(role.role_id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <RoleDialog
        open={openDialog}
        onClose={handleCloseDialog}
        mode={dialogMode}
        selectedRole={selectedRole || undefined}
        createdBy={createdBy}
      />
      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this role? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Roles;
